import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { Plus, Trash, PencilSimple, X } from "@phosphor-icons/react";

const empty = { name: "", code: "", address: "", gstin: "", state: "", state_code: "", phone: "", city: "", pin: "" };

export default function Stores() {
  const { stores, refreshStores } = useAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/stores");
    setList(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/stores/${editing.id}`, {
          name: editing.name, code: editing.code, address: editing.address,
          gstin: editing.gstin, state: editing.state, state_code: editing.state_code, phone: editing.phone,
          city: editing.city || "", pin: editing.pin || "",
        });
      } else {
        await api.post("/stores", editing);
      }
      setEditing(null);
      load();
      refreshStores();
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete store "${s.name}"?\n\nThis is permanent.`)) return;
    try {
      await api.delete(`/stores/${s.id}`);
      load();
      refreshStores();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      // If backend reports existing-data cascade block, offer force-delete.
      if (e.response?.status === 400 && /force=true/i.test(String(detail))) {
        const forceOk = window.confirm(
          `${detail}\n\n` +
          `Press OK to FORCE DELETE this store AND wipe every related sale, invoice, inward, dispatch, reconciliation, day-close and inventory row.\n\n` +
          `This cannot be undone.`
        );
        if (!forceOk) return;
        try {
          await api.delete(`/stores/${s.id}?force=true`);
          load();
          refreshStores();
          alert(`Store "${s.name}" and all its data have been deleted.`);
        } catch (e2) {
          alert("Force delete failed: " + (e2.response?.data?.detail || e2.message));
        }
      } else {
        alert("Delete failed: " + detail);
      }
    }
  };

  return (
    <Shell>
      <PageHeader
        title="Stores"
        subtitle="Manage retail locations. Each store has its own dashboard, inventory and billing."
        testId="stores-header"
        actions={<button onClick={() => setEditing({ ...empty })} data-testid="add-store-btn" className="inline-flex items-center gap-2 h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md"><Plus size={16} weight="bold" />New Store</button>}
      />
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="stores-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Code</th>
                <th className="text-left px-4 py-3 font-semibold">GSTIN</th>
                <th className="text-left px-4 py-3 font-semibold">State</th>
                <th className="text-left px-4 py-3 font-semibold">Phone</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400">No stores yet. Click "New Store" to add your first location.</td></tr>}
              {list.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{s.name}</td>
                  <td className="px-4 py-3 font-mono-num">{s.code}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-600">{s.gstin || "—"}</td>
                  <td className="px-4 py-3">{s.state} {s.state_code && <span className="text-xs text-slate-500">({s.state_code})</span>}</td>
                  <td className="px-4 py-3 font-mono-num">{s.phone || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing({ ...s })} data-testid={`edit-store-${s.code}`} className="p-1.5 text-slate-600 hover:text-slate-900 mr-1"><PencilSimple size={15} /></button>
                    <button onClick={() => del(s)} data-testid={`del-store-${s.code}`} className="p-1.5 text-red-600 hover:text-red-700"><Trash size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="store-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="font-display font-bold text-lg">{editing.id ? "Edit Store" : "New Store"}</div>
              <button onClick={() => setEditing(null)} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {[
                ["name", "Name", "text", "col-span-2"],
                ["code", "Code (e.g. BLR01)", "text"],
                ["phone", "Phone", "text"],
                ["gstin", "GSTIN", "text", "col-span-2"],
                ["state", "State", "text"],
                ["state_code", "State Code", "text"],
                ["city", "City (for e-Invoice)", "text"],
                ["pin", "PIN Code (for e-Invoice)", "text"],
                ["address", "Address", "text", "col-span-2"],
              ].map(([k, label, type, cls]) => (
                <div key={k} className={cls || ""}>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">{label}</label>
                  <input
                    type={type}
                    value={editing[k] || ""}
                    onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                    data-testid={`store-field-${k}`}
                    className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
              <button onClick={save} data-testid="save-store-btn" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md">Save</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
